import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Coffee, LogIn, LogOut, TrendingUp, Calendar, CloudOff, Pause, Play } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { StatusPill, SITE_LABEL } from "@/components/Layout";
import { UpcomingHolidaysCard } from "@/components/UpcomingHolidaysCard";
import { MyRosterCard } from "@/components/MyRosterCard";
import { toast } from "sonner";
import {
  enqueueClockRequest,
  flushPending,
  getPending,
  startAutoFlush,
  subscribePending,
} from "@/lib/offlineClockQueue";

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [recent, setRecent] = useState([]);
  const [balances, setBalances] = useState([]);
  const [pendingLeave, setPendingLeave] = useState(0);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  const [pendingQueue, setPendingQueue] = useState([]);

  const load = useCallback(async () => {
    const [s, sh, lb, lm] = await Promise.all([
      api.get("/clock/status"),
      api.get("/shifts/mine?range=week"),
      api.get("/leave/balances"),
      api.get("/leave/mine"),
    ]);
    setStatus(s.data);
    setRecent(sh.data.shifts);
    setBalances(lb.data.balances);
    setPendingLeave(lm.data.leave.filter((l) => l.status === "PENDING").length);
  }, []);

  // Refresh the queue badge whenever the queue changes.
  const refreshQueue = useCallback(async () => {
    try {
      setPendingQueue(await getPending());
    } catch (err) {
      // IDB read can fail in private-browsing modes (Safari before iOS 16);
      // log but never block the UI — the queue badge just stays empty.
      // eslint-disable-next-line no-console -- intentional dev/operator diagnostic
      if (typeof console !== "undefined") console.warn("Offline-queue read failed:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    load().catch(() => {});
    const tick = setInterval(() => setNow(new Date()), 5000);
    refreshQueue();
    const unsub = subscribePending(async () => {
      await refreshQueue();
      // Reload status whenever the queue drains (a flush may have just
      // produced a new shift on the server).
      load().catch(() => {});
    });
    // Auto-flush on online/focus/visibilitychange + reload status when
    // anything actually got sent.
    startAutoFlush(API_BASE, () => { load().catch(() => {}); });
    return () => { clearInterval(tick); unsub(); };
  }, [load, refreshQueue]);

  const clockAction = async () => {
    setBusy(true);
    const kind = status?.clocked_in ? "out" : "in";
    try {
      // Always generate a client_request_id so retries are safe even online.
      const cid = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const body = { client_request_id: cid, queued_at: new Date().toISOString() };
      try {
        await api.post(`/clock/${kind}`, body);
        toast.success(kind === "out" ? "Clocked out. Great work today!" : "You're clocked in. Brew well!");
      } catch (err) {
        const offline = !navigator.onLine || err?.code === "ERR_NETWORK" || err?.message === "Network Error";
        if (offline) {
          const row = await enqueueClockRequest({ kind });
          // Optimistic UI — surface the banner immediately. (The
          // subscribePending callback also fires, but we don't want to
          // race React state updates with toast animations.)
          setPendingQueue((q) => [...q.filter((r) => r.client_request_id !== row.client_request_id), row]);
          toast(`Saved offline — we'll sync your clock-${kind} the moment you're back online.`, {
            icon: "📴",
            duration: 6000,
          });
        } else {
          throw err;
        }
      }
      await load();
    } catch (e) {
      const code = e?.response?.data?.error;
      if (code === "wifi_restricted_relay") {
        toast.error(
          e?.response?.data?.message ||
            "A privacy relay is hiding your IP. Disable iCloud Private Relay or Cloudflare WARP, then try again.",
          { duration: 14000 },
        );
      } else if (code === "wifi_restricted") {
        toast.error(
          e?.response?.data?.message ||
            "Please connect to the Coffee Lovers Café Wi-Fi to clock in or out.",
          { duration: 8000 },
        );
      } else {
        toast.error(e?.response?.data?.detail || "Something went wrong");
      }
    } finally {
      setBusy(false);
    }
  };

  const flushNow = async () => {
    const res = await flushPending(API_BASE);
    await refreshQueue();
    await load().catch(() => {});
    if (res.sent > 0) toast.success(`Synced ${res.sent} pending clock entr${res.sent === 1 ? "y" : "ies"}.`);
    else if (pendingQueue.length > 0) toast.error("Still offline — try again in a moment.");
  };

  const breakAction = async () => {
    if (!status?.clocked_in) return;
    setBusy(true);
    const onBreak = !!status.on_break;
    try {
      if (onBreak) {
        await api.post("/clock/break/end", {});
        toast.success("Welcome back! Break ended.");
      } else {
        await api.post("/clock/break/start", {});
        toast(
          "Break started — we'll buzz you at 25, 30, 35 & 45 min.",
          { icon: "☕", duration: 5000 },
        );
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update break status.");
    } finally {
      setBusy(false);
    }
  };

  const inSince = status?.active_shift?.clock_in_ts
    ? fmtTime(status.active_shift.clock_in_ts)
    : null;
  const liveHours = status?.clocked_in
    ? ((now - new Date(status.active_shift.clock_in_ts)) / 3600000).toFixed(2)
    : null;

  // Break-state derivations.
  const onBreak = !!status?.on_break;
  const breakUsed = !!status?.break_used;
  const breakStartTs = status?.active_shift?.break_start_ts;
  const breakEndTs = status?.active_shift?.break_end_ts;
  const liveBreakMin = onBreak && breakStartTs
    ? Math.max(0, Math.floor((now - new Date(breakStartTs)) / 60000))
    : null;
  const liveBreakSec = onBreak && breakStartTs
    ? Math.max(0, Math.floor((now - new Date(breakStartTs)) / 1000))
    : null;
  // Server enforces a 30s min before End Break clears (anti-double-tap).
  const endBreakLockedFor = onBreak && liveBreakSec !== null
    ? Math.max(0, 30 - liveBreakSec)
    : 0;
  const completedBreakMin = (breakStartTs && breakEndTs)
    ? Math.max(0, Math.round((new Date(breakEndTs) - new Date(breakStartTs)) / 60000))
    : null;
  // Color the live timer: green ≤25, amber 26–30, orange 31–35, red >35.
  const breakTone = (() => {
    if (liveBreakMin === null) return "";
    if (liveBreakMin <= 25) return "text-emerald-300";
    if (liveBreakMin <= 30) return "text-[#C9A84C]";
    if (liveBreakMin <= 35) return "text-orange-300";
    return "text-red-300";
  })();

  // Three-state break button — derived once instead of inline ternary chains
  // so the JSX below stays readable.
  const breakButton = (() => {
    const baseClasses = "h-14 w-full md:min-w-[220px] text-base font-semibold rounded-2xl border-2 transition-all active:scale-95";
    const dimClasses = "border-white/20 bg-white/5 text-white/40 cursor-not-allowed";
    if (onBreak && endBreakLockedFor > 0) {
      return {
        disabled: true,
        classes: `${baseClasses} ${dimClasses}`,
        icon: <Pause className="h-5 w-5 mr-2" />,
        label: `End Break · in ${endBreakLockedFor}s`,
      };
    }
    if (onBreak) {
      return {
        disabled: false,
        classes: `${baseClasses} border-emerald-300 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25`,
        icon: <Play className="h-5 w-5 mr-2" />,
        label: "End Break",
      };
    }
    if (breakUsed) {
      return {
        disabled: true,
        classes: `${baseClasses} ${dimClasses}`,
        icon: <Coffee className="h-5 w-5 mr-2" />,
        label: "Break taken",
      };
    }
    return {
      disabled: false,
      classes: `${baseClasses} border-white/30 bg-white/10 text-white hover:bg-white/20`,
      icon: <Pause className="h-5 w-5 mr-2" />,
      label: "Start 30-min Break",
    };
  })();

  const annual = balances.find((b) => b.leave_type === "ANNUAL");
  const sick = balances.find((b) => b.leave_type === "SICK");

  return (
    <div className="space-y-8" data-testid="employee-dashboard">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm text-[#C9A84C] uppercase tracking-[0.25em] font-semibold">
            {SITE_LABEL[user?.primary_site] || "Cafe"} · {new Date().toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#1A2744] mt-2">
            Kia ora, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-gray-500 mt-2">
            Tap the big button when you start or finish your shift.
          </p>
        </div>
      </header>

      {/* Offline queue banner — shown only when there are pending taps */}
      {pendingQueue.length > 0 && (
        <div
          data-testid="offline-queue-banner"
          className="flex items-center justify-between gap-4 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-3 text-sm text-amber-900 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <CloudOff className="h-5 w-5 shrink-0" strokeWidth={1.8} />
            <div>
              <div className="font-semibold">
                {pendingQueue.length} clock {pendingQueue.length === 1 ? "tap" : "taps"} waiting to sync
              </div>
              <div className="text-xs text-amber-800/80">
                Saved on your device — we'll send them the moment you're back online.
              </div>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={flushNow}
            className="border-amber-400 text-amber-900 hover:bg-amber-100"
            data-testid="offline-queue-sync-now"
          >
            Sync now
          </Button>
        </div>
      )}

      {/* Clock action card */}
      <Card className="border-0 shadow-[0_8px_32px_rgba(26,39,68,0.12)] overflow-hidden">
        <CardContent className="p-0">
          <div className="bg-gradient-to-br from-[#1A2744] to-[#263A63] text-white p-6 sm:p-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 text-[#C9A84C] text-xs uppercase tracking-[0.25em] font-semibold mb-2">
                  <Coffee className="h-4 w-4" strokeWidth={1.8} />
                  Shift status
                </div>
                <div className="font-heading text-3xl sm:text-4xl font-bold" data-testid="clock-status-title">
                  {status?.clocked_in
                    ? `You are clocked IN since ${inSince}`
                    : "You are clocked OUT"}
                </div>
                {status?.clocked_in && (
                  <div className="mt-2 text-white/70 text-sm">
                    {liveHours} hrs on this shift · running live
                  </div>
                )}
                {onBreak && liveBreakMin !== null && (
                  <div
                    className={`mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold backdrop-blur-sm ${breakTone}`}
                    data-testid="break-live-timer"
                  >
                    <Pause className="h-4 w-4" strokeWidth={2} />
                    On break · {liveBreakMin} of 30 min
                    {liveBreakMin > 30 && (
                      <span className="ml-1 text-xs uppercase tracking-wider opacity-80">
                        +{liveBreakMin - 30} over
                      </span>
                    )}
                  </div>
                )}
                {!onBreak && breakUsed && completedBreakMin !== null && (
                  <div
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold text-white/80"
                    data-testid="break-completed-pill"
                  >
                    <Coffee className="h-3 w-3" strokeWidth={2} />
                    Break taken · {completedBreakMin} min
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-3 w-full md:w-auto">
                <Button
                  disabled={busy}
                  onClick={clockAction}
                  className={`h-24 md:h-20 w-full md:min-w-[220px] text-2xl md:text-xl font-bold rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.25)] transition-all active:scale-95 ${
                    status?.clocked_in
                      ? "bg-red-500 hover:bg-red-600 text-white"
                      : "bg-[#C9A84C] hover:bg-[#E6C568] text-[#1A2744]"
                  }`}
                  data-testid="clock-action-button"
                >
                  {status?.clocked_in ? (
                    <><LogOut className="h-7 w-7 mr-2" /> Clock Out</>
                  ) : (
                    <><LogIn className="h-7 w-7 mr-2" /> Clock In</>
                  )}
                </Button>

                {/* Break controls — only visible while clocked-in */}
                {status?.clocked_in && (
                  <Button
                    disabled={busy || breakButton.disabled}
                    onClick={breakAction}
                    variant="outline"
                    className={breakButton.classes}
                    data-testid="break-action-button"
                  >
                    {breakButton.icon} {breakButton.label}
                  </Button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-8 pt-8 border-t border-white/10">
              <div>
                <div className="text-white/60 text-xs uppercase tracking-widest">Today</div>
                <div className="font-heading text-3xl font-bold mt-1" data-testid="today-hours">
                  {status?.today_hours ?? "0"} hrs
                </div>
              </div>
              <div>
                <div className="text-white/60 text-xs uppercase tracking-widest">Week to date</div>
                <div className="font-heading text-3xl font-bold mt-1" data-testid="week-hours">
                  {status?.week_hours ?? "0"} hrs
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Balance + leave */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Annual Leave</div>
              <Calendar className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.5} />
            </div>
            <div className="mt-3 font-heading text-4xl font-bold text-[#1A2744]" data-testid="annual-balance">
              {annual ? (annual.entitled_days - annual.used_days).toFixed(1) : "0"}
            </div>
            <div className="text-sm text-gray-500 mt-1">hours remaining of {Number(annual?.entitled_days ?? 0).toFixed(1)}</div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Sick Leave</div>
              <TrendingUp className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.5} />
            </div>
            <div className="mt-3 font-heading text-4xl font-bold text-[#1A2744]" data-testid="sick-balance">
              {sick ? (sick.entitled_days - sick.used_days).toFixed(1) : "0"}
            </div>
            <div className="text-sm text-gray-500 mt-1">hours remaining of {Number(sick?.entitled_days ?? 0).toFixed(1)}</div>
          </CardContent>
        </Card>
        <Card className="bg-[#1A2744] text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-[#C9A84C] font-semibold">Pending requests</div>
              <Clock className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.5} />
            </div>
            <div className="mt-3 font-heading text-4xl font-bold" data-testid="pending-leave-count">{pendingLeave}</div>
            <Link to="/leave" className="text-sm text-[#C9A84C] mt-1 inline-block hover:underline">
              View my leave →
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Recent shifts */}
      <Card className="border-gray-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-bold text-[#1A2744]">Recent shifts (this week)</h3>
            <Link to="/timesheet" className="text-sm text-[#C9A84C] hover:underline">Full timesheet →</Link>
          </div>
          {recent.length === 0 && (
            <div className="text-sm text-gray-500 py-6 text-center">
              No shifts recorded this week yet.
            </div>
          )}
          <div className="divide-y divide-gray-100">
            {recent.slice(0, 7).map((s) => (
              <div key={s.id} className="py-3 grid grid-cols-2 sm:grid-cols-5 gap-2 items-center text-sm">
                <div className="text-[#1A2744] font-medium">{fmtDate(s.clock_in_ts)}</div>
                <div className="text-gray-600">{fmtTime(s.clock_in_ts)} → {fmtTime(s.clock_out_ts)}</div>
                <div className="text-gray-600">
                  {s.total_minutes ? (s.total_minutes / 60).toFixed(2) : "—"} hrs
                  {(s.break_minutes > 0 || s.break_start_ts) && (
                    <span
                      className="ml-1 text-[10px] text-amber-700"
                      title={s.break_start_ts ? `${s.break_minutes || 0} min break (tracked)` : `Auto-deducted ${s.break_minutes} min break`}
                    >
                      (-{s.break_minutes || 0}m break)
                    </span>
                  )}
                </div>
                <div className="text-gray-500 text-xs">{SITE_LABEL[s.site]}</div>
                <div><StatusPill status={s.status} /></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <UpcomingHolidaysCard site={user?.primary_site} testid="employee-upcoming-holidays" />

      <MyRosterCard />
    </div>
  );
}
